import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const WIDTH_PX = {
  'w-64': 256,
  'w-72': 288,
  'w-80': 320,
};

/**
 * Hover/focus tooltip that portals to document.body so overflow:auto ancestors
 * (e.g. PolicyPlan location details) cannot clip the text.
 */
export default function InfoTooltip({ label, children, align = 'left', widthClass = 'w-80' }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 320 });
  const btnRef = useRef(null);

  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const preferred = WIDTH_PX[widthClass] || 320;
    const width = Math.min(preferred, Math.max(176, window.innerWidth - 16));
    let left = align === 'right' ? r.right - width : r.left;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    let top = r.bottom + 8;
    const estimatedHeight = 120;
    if (top + estimatedHeight > window.innerHeight - 8) {
      top = Math.max(8, r.top - estimatedHeight - 8);
    }
    setCoords({ top, left, width });
  }, [align, widthClass]);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onReposition = () => place();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, place]);

  const show = () => setOpen(true);
  const hide = () => setOpen(false);

  return (
    <span className="inline-flex flex-shrink-0">
      <button
        ref={btnRef}
        type="button"
        aria-label={label}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="flex h-5 w-5 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
      >
        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            role="tooltip"
            className={`${widthClass} pointer-events-none fixed z-[9999] rounded-lg border border-gray-200 bg-white p-3 text-xs leading-relaxed text-gray-600 shadow-lg whitespace-normal break-words`}
            style={{ top: coords.top, left: coords.left, width: coords.width }}
          >
            {children}
          </div>,
          document.body
        )}
    </span>
  );
}
